import type { FC, ReactNode } from 'react'
import type { Invoice } from '@/types'

import { useEffect, useState } from 'react'
import { Copy } from 'lucide-react'
import { formatRupiah } from '@/lib/format'
import { formatDate } from '@/lib/date'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/generals/logo'
import { InvoiceStatusBadge } from '@/components/dashboard/status-badge'
import { isOverdue } from '@/data/invoices'
import { getPlan } from '@/data/plans'
import { CYCLE_LABELS, PAYMENT_METHOD_LABELS } from '@/constants/dashboard'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

interface InvoiceDialogProps {
    invoice: Invoice | null
    onOpenChange: (open: boolean) => void
    onCopyNumber: (invoice: Invoice) => void
    onMarkPaid: (invoice: Invoice) => void
}

const VAT_RATE = 0.11

export const InvoiceDialog: FC<InvoiceDialogProps> = ({ invoice, onOpenChange, onCopyNumber, onMarkPaid }): ReactNode => {
    const [displayed, setDisplayed] = useState<Invoice | null>(invoice)

    useEffect(() => {
        if (invoice) setDisplayed(invoice)
    }, [invoice])

    const current = invoice ?? displayed
    const plan = current ? getPlan(current.plan) : null
    const months = current?.cycle === 'annual' ? 12 : 1
    const basePrice = current && plan ? (current.cycle === 'annual' ? plan.annualPrice : plan.price) * months : 0
    const extraSeats = current && plan ? Math.max(0, current.seats - plan.includedSeats) : 0
    const seatsTotal = current ? Math.max(0, current.amount - basePrice) : 0
    const vat = current ? Math.round((current.amount * VAT_RATE) / (1 + VAT_RATE)) : 0
    const canMarkPaid = current?.status === 'pending' || current?.status === 'failed'

    return (
        <Dialog open={invoice !== null} onOpenChange={onOpenChange}>
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-xl'>
                {current && plan && (
                    <>
                        <DialogHeader className='flex-row items-start justify-between gap-4 pr-8'>
                            <div className='flex items-center gap-3'>
                                <LogoMark className='size-10' />
                                <div>
                                    <DialogTitle className='text-base font-semibold'>
                                        Faktur {current.number}
                                    </DialogTitle>
                                    <DialogDescription>
                                        Arunika, Jakarta Selatan
                                    </DialogDescription>
                                </div>
                            </div>
                            <InvoiceStatusBadge status={current.status} />
                        </DialogHeader>

                        <div className='grid gap-4 rounded-lg border p-4 text-sm sm:grid-cols-2'>
                            <div>
                                <p className='text-xs font-medium text-muted-foreground'>Ditagihkan kepada</p>
                                <p className='mt-1.5 font-semibold text-foreground'>{current.company}</p>
                                <p className='text-muted-foreground'>{current.contact}</p>
                                <p className='text-muted-foreground'>{current.email}</p>
                                <p className='text-muted-foreground'>{current.city}</p>
                            </div>
                            <dl className='space-y-1.5'>
                                <div className='flex justify-between gap-3'>
                                    <dt className='text-muted-foreground'>Diterbitkan</dt>
                                    <dd className='font-medium text-foreground'>{formatDate(current.issuedAt)}</dd>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <dt className='text-muted-foreground'>Jatuh tempo</dt>
                                    <dd className='font-medium text-foreground'>
                                        {formatDate(current.dueAt)}
                                        {isOverdue(current) && <span className='ml-1.5 text-xs text-destructive'>terlambat</span>}
                                    </dd>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <dt className='text-muted-foreground'>Dibayar</dt>
                                    <dd className='font-medium text-foreground'>{current.paidAt ? formatDate(current.paidAt) : 'Belum'}</dd>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <dt className='text-muted-foreground'>Metode</dt>
                                    <dd className='font-medium text-foreground'>{PAYMENT_METHOD_LABELS[current.method]}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className='overflow-hidden rounded-lg border'>
                            <table className='w-full text-sm'>
                                <thead className='bg-muted/60 text-xs text-muted-foreground'>
                                    <tr>
                                        <th scope='col' className='px-4 py-2 text-left font-medium'>Deskripsi</th>
                                        <th scope='col' className='px-4 py-2 text-right font-medium'>Jumlah</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y'>
                                    <tr>
                                        <td className='px-4 py-3'>
                                            <p className='font-medium text-foreground'>Paket {plan.name}</p>
                                            <p className='text-xs text-muted-foreground'>
                                                {CYCLE_LABELS[current.cycle]}, termasuk {plan.includedSeats} pengguna
                                            </p>
                                        </td>
                                        <td className='px-4 py-3 text-right text-foreground tabular-nums'>{formatRupiah(Math.min(basePrice, current.amount))}</td>
                                    </tr>
                                    {extraSeats > 0 && seatsTotal > 0 && (
                                        <tr>
                                            <td className='px-4 py-3'>
                                                <p className='font-medium text-foreground'>Pengguna tambahan</p>
                                                <p className='text-xs text-muted-foreground'>{extraSeats} pengguna</p>
                                            </td>
                                            <td className='px-4 py-3 text-right text-foreground tabular-nums'>{formatRupiah(seatsTotal)}</td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot className='border-t bg-muted/30'>
                                    <tr>
                                        <td className='px-4 pt-3 text-muted-foreground'>Termasuk PPN 11%</td>
                                        <td className='px-4 pt-3 text-right text-muted-foreground tabular-nums'>{formatRupiah(vat)}</td>
                                    </tr>
                                    <tr>
                                        <td className='px-4 pt-1 pb-3 font-semibold text-foreground'>Total</td>
                                        <td className='px-4 pt-1 pb-3 text-right font-heading text-base font-semibold text-foreground tabular-nums'>{formatRupiah(current.amount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <DialogFooter>
                            <Button variant='outline' onClick={() => onCopyNumber(current)}>
                                <Copy data-icon='inline-start' />
                                Salin nomor
                            </Button>
                            {canMarkPaid && (
                                <Button onClick={() => onMarkPaid(current)}>
                                    Tandai lunas
                                </Button>
                            )}
                            <DialogClose render={<Button variant={canMarkPaid ? 'ghost' : 'default'} />}>
                                Tutup
                            </DialogClose>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
